import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { projectsApi } from '../utils/api';
import { useAuthStore } from '../store/auth';
import {
    Sparkles,
    ArrowRight,
    ArrowLeft,
    Building2,
    Target,
    Rocket,
    CheckCircle,
    Search,
    Share2,
    Mail,
    Megaphone,
    BarChart3,
    Code,
    Briefcase,
    Globe
} from 'lucide-react';

type Step = 'welcome' | 'business' | 'goals' | 'project' | 'complete';

interface OnboardingData {
    companyName: string;
    companySize: string;
    industry: string;
    website: string;
    goals: string[];
    projectName: string;
}

export default function Onboarding() {
    const navigate = useNavigate();
    const { user, setOnboardingComplete, skipOnboarding } = useAuthStore();
    const [step, setStep] = useState<Step>('welcome');
    const [data, setData] = useState<OnboardingData>({
        companyName: '',
        companySize: '',
        industry: '',
        website: '',
        goals: [],
        projectName: ''
    });

    const createProjectMutation = useMutation({
        mutationFn: (projectData: any) => projectsApi.create(projectData),
        onSuccess: () => {
            setOnboardingComplete();
            setStep('complete');
        },
        onError: (error: any) => {
            console.error('Project creation failed:', error);
            // Still complete onboarding even if project fails
            setOnboardingComplete();
            setStep('complete');
        }
    });

    const steps: Step[] = ['welcome', 'business', 'goals', 'project', 'complete'];
    const currentIndex = steps.indexOf(step);
    const progress = ((currentIndex) / (steps.length - 1)) * 100;

    const goNext = () => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < steps.length) {
            setStep(steps[nextIndex]);
        }
    };

    const goBack = () => {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
            setStep(steps[prevIndex]);
        }
    };

    const handleSkip = () => {
        skipOnboarding();
        navigate('/dashboard');
    };

    // Map display industry to backend enum value
    const mapIndustry = (industry: string): string => {
        const industryMap: Record<string, string> = {
            'SaaS / Software': 'saas',
            'E-commerce': 'ecommerce',
            'Agency / Consulting': 'agency',
            'Healthcare': 'healthcare',
            'Finance': 'finance',
            'Education': 'education',
            'Real Estate': 'other',
            'Media / Entertainment': 'other',
            'Manufacturing': 'other',
            'Other': 'other'
        };
        return industryMap[industry] || 'other';
    };

    const handleFinish = async () => {
        // Create first project
        try {
            await createProjectMutation.mutateAsync({
                name: data.projectName || `${data.companyName || 'My'} Project`,
                websiteUrl: data.website || undefined,
                industry: mapIndustry(data.industry),
                goals: data.goals
            });
        } catch (error) {
            // Error handled in mutation
        }
    };

    const goalOptions = [
        { id: 'seo', label: 'Improve SEO & Rankings', icon: Search, color: 'from-blue-500 to-cyan-500' },
        { id: 'social', label: 'Grow Social Media', icon: Share2, color: 'from-pink-500 to-rose-500' },
        { id: 'ads', label: 'Run Paid Ads', icon: Megaphone, color: 'from-orange-500 to-amber-500' },
        { id: 'email', label: 'Email Marketing', icon: Mail, color: 'from-green-500 to-emerald-500' },
        { id: 'analytics', label: 'Track Analytics', icon: BarChart3, color: 'from-purple-500 to-violet-500' },
        { id: 'automation', label: 'Automate Workflows', icon: Rocket, color: 'from-red-500 to-orange-500' },
        { id: 'devai', label: 'Developer AI Tools', icon: Code, color: 'from-cyan-500 to-blue-500' },
        { id: 'bizops', label: 'Business Operations', icon: Briefcase, color: 'from-amber-500 to-yellow-500' }
    ];

    const industryOptions = [
        'SaaS / Software',
        'E-commerce',
        'Agency / Consulting',
        'Healthcare',
        'Finance',
        'Education',
        'Real Estate',
        'Media / Entertainment',
        'Manufacturing',
        'Other'
    ];

    const companySizeOptions = [
        'Just me',
        '2-10 employees',
        '11-50 employees',
        '51-200 employees',
        '201-500 employees',
        '500+ employees'
    ];

    return (
        <div className="min-h-screen bg-dark-950 flex flex-col">
            {/* Progress Bar */}
            {step !== 'welcome' && step !== 'complete' && (
                <div className="fixed top-0 left-0 right-0 h-1 bg-dark-800 z-50">
                    <div
                        className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}

            {/* Header */}
            <header className="p-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-bold">aiOps</span>
                </div>
            </header>

            {/* Content */}
            <main className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-2xl">

                    {/* Step: Welcome */}
                    {step === 'welcome' && (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                                <Sparkles className="w-10 h-10 text-white" />
                            </div>
                            <h1 className="text-4xl font-bold mb-4">
                                Welcome to aiOps, {user?.name?.split(' ')[0]}! 🎉
                            </h1>
                            <p className="text-xl text-dark-300 mb-8">
                                Let's set up your AI-powered workspace in just a few steps.
                            </p>
                            <button
                                onClick={goNext}
                                className="btn btn-primary text-lg px-8 py-4 inline-flex items-center gap-2"
                            >
                                Get Started
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <p className="text-dark-500 text-sm mt-6">Takes about 2 minutes</p>
                            <button
                                onClick={handleSkip}
                                className="mt-4 text-dark-400 hover:text-dark-300 text-sm underline"
                            >
                                Skip for now, I'll explore on my own
                            </button>
                        </div>
                    )}

                    {/* Step: Business Info */}
                    {step === 'business' && (
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-primary-400" />
                                </div>
                                <span className="text-dark-400 text-sm">Step 1 of 4</span>
                            </div>
                            <h2 className="text-3xl font-bold mb-2">Tell us about your business</h2>
                            <p className="text-dark-400 mb-8">This helps us personalize your experience.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="label">Company Name</label>
                                    <input
                                        type="text"
                                        value={data.companyName}
                                        onChange={(e) => setData({ ...data, companyName: e.target.value })}
                                        className="input"
                                        placeholder="Acme Inc."
                                    />
                                </div>

                                <div>
                                    <label className="label">Website (optional)</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                                        <input
                                            type="url"
                                            value={data.website}
                                            onChange={(e) => setData({ ...data, website: e.target.value })}
                                            className="input pl-10"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="label">Industry</label>
                                    <select
                                        value={data.industry}
                                        onChange={(e) => setData({ ...data, industry: e.target.value })}
                                        className="input"
                                    >
                                        <option value="">Select industry</option>
                                        {industryOptions.map((industry) => (
                                            <option key={industry} value={industry}>{industry}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Company Size</label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {companySizeOptions.map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => setData({ ...data, companySize: size })}
                                                className={`p-3 rounded-lg border text-sm transition-all ${data.companySize === size
                                                    ? 'border-primary-500 bg-primary-500/10 text-white'
                                                    : 'border-dark-700 hover:border-dark-600 text-dark-300'
                                                    }`}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-8">
                                <button onClick={goBack} className="btn btn-secondary flex items-center gap-2">
                                    <ArrowLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <button onClick={goNext} className="btn btn-primary flex items-center gap-2">
                                    Continue
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step: Goals */}
                    {step === 'goals' && (
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                                    <Target className="w-5 h-5 text-primary-400" />
                                </div>
                                <span className="text-dark-400 text-sm">Step 2 of 3</span>
                            </div>
                            <h2 className="text-3xl font-bold mb-2">What are your goals?</h2>
                            <p className="text-dark-400 mb-8">Select all that apply. We'll enable the right modules for you.</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {goalOptions.map((goal) => {
                                    const isSelected = data.goals.includes(goal.id);
                                    return (
                                        <button
                                            key={goal.id}
                                            onClick={() => {
                                                setData({
                                                    ...data,
                                                    goals: isSelected
                                                        ? data.goals.filter(g => g !== goal.id)
                                                        : [...data.goals, goal.id]
                                                });
                                            }}
                                            className={`p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${isSelected
                                                ? 'border-primary-500 bg-primary-500/10'
                                                : 'border-dark-700 hover:border-dark-600'
                                                }`}
                                        >
                                            <div className={`w-12 h-12 bg-gradient-to-br ${goal.color} rounded-xl flex items-center justify-center`}>
                                                <goal.icon className="w-6 h-6 text-white" />
                                            </div>
                                            <span className="font-medium">{goal.label}</span>
                                            {isSelected && (
                                                <CheckCircle className="w-5 h-5 text-primary-400 ml-auto" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center justify-between mt-8">
                                <button onClick={goBack} className="btn btn-secondary flex items-center gap-2">
                                    <ArrowLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <button
                                    onClick={goNext}
                                    disabled={data.goals.length === 0}
                                    className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    Continue
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step: Create Project */}
                    {step === 'project' && (
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                                    <Rocket className="w-5 h-5 text-primary-400" />
                                </div>
                                <span className="text-dark-400 text-sm">Step 3 of 3</span>
                            </div>
                            <h2 className="text-3xl font-bold mb-2">Create your first project</h2>
                            <p className="text-dark-400 mb-8">Give your project a name to get started.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="label">Project Name</label>
                                    <input
                                        type="text"
                                        value={data.projectName}
                                        onChange={(e) => setData({ ...data, projectName: e.target.value })}
                                        className="input"
                                        placeholder={data.companyName ? `${data.companyName} Marketing` : 'My First Project'}
                                    />
                                </div>

                                {/* Summary */}
                                <div className="card p-6 bg-dark-800/50">
                                    <h3 className="font-semibold mb-4">Setup Summary</h3>
                                    <div className="space-y-3 text-sm">
                                        {data.companyName && (
                                            <div className="flex justify-between">
                                                <span className="text-dark-400">Company</span>
                                                <span>{data.companyName}</span>
                                            </div>
                                        )}
                                        {data.industry && (
                                            <div className="flex justify-between">
                                                <span className="text-dark-400">Industry</span>
                                                <span>{data.industry}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-dark-400">Goals</span>
                                            <span>{data.goals.length} selected</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-8">
                                <button onClick={goBack} className="btn btn-secondary flex items-center gap-2">
                                    <ArrowLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <button
                                    onClick={handleFinish}
                                    disabled={createProjectMutation.isPending}
                                    className="btn btn-primary flex items-center gap-2"
                                >
                                    {createProjectMutation.isPending ? 'Creating...' : 'Launch Project'}
                                    <Rocket className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step: Complete */}
                    {step === 'complete' && (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                                <CheckCircle className="w-10 h-10 text-white" />
                            </div>
                            <h1 className="text-4xl font-bold mb-4">You're all set! 🚀</h1>
                            <p className="text-xl text-dark-300 mb-8">
                                Your workspace is ready. Let's start building.
                            </p>

                            <div className="space-y-4">
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className="btn btn-primary text-lg px-8 py-4 w-full flex items-center justify-center gap-2"
                                >
                                    Go to Dashboard
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => navigate('/projects')}
                                    className="btn btn-secondary text-lg px-8 py-4 w-full"
                                >
                                    View Projects
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </main>
        </div>
    );
}
